// zod schemas for the project JSON (mirrors the prototype's editor state) and module recipes.
import { z } from 'zod';

const frontSegmentSchema = z.object({
  t: z.enum(['door', 'drawer', 'oven', 'open', 'niche']),
  f: z.number().positive().max(1),
  n: z.number().int().min(1).max(6).optional(),
  rod: z.union([z.number(), z.boolean()]).optional(),
  /** Appliance housed in a niche segment. */
  ap: z.enum(['nevera', 'lavadora', 'micro', 'extractor']).optional(),
});

const flag = z.union([z.literal(0), z.literal(1), z.boolean()]).optional();
/** Sink bowls (1 or 2) and cooktop burners (1 = the usual 4; 4 or 6). */
const count = z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(4), z.literal(6), z.boolean()]).optional();
/** Appliance variants: a freestanding range (its own top, no worktop) and fridge doors (2 = side by side). */
const applianceFields = {
  range: flag,
  fd: z.union([z.literal(1), z.literal(2)]).optional(),
};

/**
 * One board of a module uploaded as a 3D model (read from its meshes): name, lowest corner and size in mm
 * (x = width, y = depth from the back, z = height) and the material slot it takes.
 */
export const panelSchema = z.object({
  n: z.string().max(80),
  p: z.tuple([z.number(), z.number(), z.number()]),
  s: z.tuple([z.number().nonnegative(), z.number().nonnegative(), z.number().nonnegative()]),
  slot: z.enum(['cuerpo', 'frentes', 'trasera']).optional(),
});
/** Construction details set in the module designer (Bibliotecas → Módulos). */
const buildFields = {
  /** Cubre falta superior: a single board from the top of the uppers to the ceiling. */
  cover: flag,
  /** Shelves inside the box; omitted = the usual count for its fronts. */
  shelves: z.number().int().min(0).max(12).optional(),
  /** Vertical dividers (equal compartments). */
  div: z.number().int().min(0).max(6).optional(),
  /** 0 = no back panel. */
  back: z.union([z.literal(0), z.literal(1)]).optional(),
  /** Panel by panel construction (top, bottom, sides, back and how they meet). */
  build: z
    .object({
      top: z.enum(['completo', 'travesanos', 'no']).optional(),
      topPos: z.enum(['entre', 'total']).optional(),
      bottom: z.union([z.literal(0), z.literal(1)]).optional(),
      bottomPos: z.enum(['entre', 'total']).optional(),
      left: z.union([z.literal(0), z.literal(1)]).optional(),
      right: z.union([z.literal(0), z.literal(1)]).optional(),
      backT: z.union([z.literal(6), z.literal(18)]).optional(),
      backPos: z.enum(['ranura', 'total', 'entre']).optional(),
      front: z.union([z.literal(0), z.literal(1)]).optional(),
      rail: z.number().int().min(40).max(300).optional(),
      ext: z
        .object({
          bottom: z.object({ t: z.enum(['proyecto', 'zocalo', 'patas', 'ninguno']), h: z.number().min(0).max(400).optional(), set: z.number().min(0).max(200).optional() }).optional(),
          top: z.object({ t: z.enum(['ninguno', 'cornisa']), h: z.number().min(3).max(150).optional(), over: z.number().min(0).max(200).optional() }).optional(),
          left: z.object({ t: z.enum(['ninguno', 'panel']), esp: z.number().min(3).max(60).optional(), floor: z.union([z.literal(0), z.literal(1)]).optional() }).optional(),
          right: z.object({ t: z.enum(['ninguno', 'panel']), esp: z.number().min(3).max(60).optional(), floor: z.union([z.literal(0), z.literal(1)]).optional() }).optional(),
        })
        .optional(),
    })
    .optional(),
};
/** How an uploaded model is drawn: as a native module (fronts that open) or as its own 3D model. */
const drawField = z.enum(['nativo', 'modelo']).optional();
const panelsFields = {
  /** Boards read from the uploaded 3D model; when present the despiece uses them instead of the standard box. */
  panels: z.array(panelSchema).max(300).optional(),
  /** Model size in cm (w, h, d) the panels were measured at; resizing the module scales them from it. */
  pdim: z.tuple([z.number().positive(), z.number().positive(), z.number().positive()]).optional(),
};

export const moduleSchema = z
  .object({
    id: z.number().int().positive(),
    code: z.string().min(1).max(40),
    name: z.string().min(1).max(120),
    cat: z.string().max(60).default('Mis módulos'),
    wall: z.enum(['A', 'B', 'C', 'D', 'F']),
    pos: z.number().min(0).max(5000).optional(),
    x: z.number().min(-1000).max(5000).optional(),
    y: z.number().min(-1000).max(5000).optional(),
    w: z.number().positive().max(1000),
    h: z.number().positive().max(1000),
    d: z.number().positive().max(1000),
    type: z.enum(['base', 'upper', 'tall', 'fridge', 'hood']),
    fr: z.array(frontSegmentSchema).max(12).default([]),
    rw: z.tuple([z.number().positive(), z.number().positive()]).optional(),
    sink: count,
    cook: count,
    appl: flag,
    oven: flag,
    ...applianceFields,
    open: z.enum(['der', 'izq']).optional(),
    /** Free modules only: turned this many degrees (0–359.9; 90 faces wall C, 180 wall A, 270 wall B). */
    rot: z.number().min(0).lt(360).optional(),
    /** Bottom height (cm): a wall-hung unit (upper, hood) off the 150 cm line, or a floor module raised off its plinth (0 = on the floor). */
    z: z.number().min(0).max(500).optional(),
    /** Per-module material overrides (material codes). */
    cue: z.string().max(60).optional(),
    fre: z.string().max(60).optional(),
    /** GLB url (library modules made from a 3D model). */
    glb: z.string().max(2000).optional(),
    ...panelsFields,
    draw: drawField,
    ...buildFields,
    /** Library module base price (base currency) and the width it refers to. */
    pBase: z.number().min(0).optional(),
    w0: z.number().positive().optional(),
    /** Manual price override (base currency). */
    pOv: z.number().min(0).optional(),
    libId: z.string().max(80).optional(),
    moduleVersion: z.number().int().positive().optional(),
  })
  .loose()
  .refine((m) => (m.wall === 'F' ? m.x !== undefined && m.y !== undefined : m.pos !== undefined), {
    message: 'Los módulos en muro necesitan "pos"; los de isla (F) necesitan "x" y "y".',
  });

const openingSchema = z
  .object({
    id: z.number().int(),
    t: z.enum(['ventana', 'puerta']),
    wall: z.enum(['A', 'B', 'C', 'D']),
    pos: z.number().min(0),
    w: z.number().positive(),
    h: z.number().positive(),
    z: z.number().min(0).optional(),
  })
  .loose();

const pointSchema = z
  .object({
    id: z.number().int(),
    t: z.enum(['agua', 'desague', 'elec', 'gas', 'campana']),
    wall: z.enum(['A', 'B', 'C', 'D']),
    pos: z.number().min(0),
    z: z.number().min(0).optional(),
  })
  .loose();

/** Orbit camera: azimuth and polar angle (radians), distance (m) and target point (m). */
export const camSchema = z.object({
  az: z.number().min(-20).max(20),
  polar: z.number().min(0.05).max(3.1),
  dist: z.number().min(0.2).max(60),
  target: z.tuple([z.number().min(-50).max(50), z.number().min(-50).max(50), z.number().min(-50).max(50)]),
});
export type CameraState = z.infer<typeof camSchema>;

export const priceAdjSchema = z.object({
  /** Installation % over the subtotal. */
  inst: z.number().min(0).max(100).default(8),
  /** Discount %. */
  desc: z.number().min(0).max(100).default(0),
  /** Manual final price (base currency, tax included). */
  final: z.number().min(0).nullable().default(null),
  /** Manual countertop price (base currency). */
  counter: z.number().min(0).nullable().default(null),
  /** Tax rate for this project only (0 = sin impuesto); null uses the organisation's rate. */
  taxRate: z.number().min(0).max(1).nullable().default(null),
});

export const projectDataSchema = z
  .object({
    schemaVersion: z.literal(1),
    ptype: z.enum(['cocina', 'closet', 'vestidor', 'tv']),
    pname: z.string().min(1).max(160),
    layout: z.string().max(40).optional(),
    room: z.object({ A: z.number().min(50).max(2000), B: z.number().min(50).max(2000), H: z.number().min(150).max(600) }),
    ops: z.array(openingSchema).max(40).default([]),
    pts: z.array(pointSchema).max(80).default([]),
    /** Finish of each wall: a material (board / texture of the library) or a plain colour. */
    walls: z
      .partialRecord(z.enum(['A', 'B', 'C', 'D']), z.object({ mat: z.string().max(60).optional(), color: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional() }))
      .optional(),
    /** Panels (planchas) fixed on a wall: a board of the library over a zone (cm, pos from the wall's own corner like modules). */
    panels: z
      .array(
        z.object({
          id: z.number().int().positive(),
          wall: z.enum(['A', 'B', 'C', 'D']),
          pos: z.number().min(0).max(5000),
          w: z.number().positive().max(1000),
          z: z.number().min(0).max(500),
          h: z.number().positive().max(500),
          mat: z.string().min(1).max(60),
        }),
      )
      .max(40)
      .optional(),
    appl: z.record(z.string(), z.unknown()).nullable().optional(),
    prefs: z
      .object({
        estilo: z.string().optional(),
        alacena: z.string().optional(),
        apertura: z.enum(['Jaladera', 'Gola', 'Push']).default('Jaladera'),
        /** Gap between doors and drawers (mm): what each front is cut short of its opening. */
        junta: z.number().min(0).max(10).optional(),
        zocalo: z.string().optional(),
        presupuesto: z.number().optional(),
        /**
         * Cubre falta superior in the generated layout: on, its height (omitted = up to the ceiling), the gap left
         * under the ceiling and its depth (omitted = as deep as the uppers), in cm.
         */
        cubre: z
          .object({
            on: z.boolean(),
            h: z.number().min(3).max(150).optional(),
            gap: z.number().min(0).max(10).optional(),
            d: z.number().min(20).max(70).optional(),
          })
          .optional(),
        /** Own modules for the generated layout: standard code → catalogue code ('' keeps the standard one). */
        mods: z.record(z.string().max(60), z.string().max(60)).optional(),
      })
      .loose()
      .default({ apertura: 'Jaladera' }),
    closet: z.record(z.string(), z.unknown()).optional(),
    /** Mueble de TV preferences (Especificaciones paso 5): TV size, towers, shelves, panel, upper cabinet. */
    tv: z.record(z.string(), z.unknown()).optional(),
    /** Cameras chosen by hand in the approval gallery (also used by the PDF and the client page). */
    cams: z
      .object({
        persp: camSchema.optional(),
        /** Detail view: which module (id) and, optionally, a hand-made camera. */
        det: z.object({ mod: z.number().int().optional(), cam: camSchema.optional() }).optional(),
        /** Extra views added to the gallery by hand: each one a named camera. */
        views: z
          .array(z.object({ id: z.string().regex(/^[a-z0-9]{1,12}$/), name: z.string().trim().min(1).max(60), cam: camSchema }))
          .max(12)
          .optional(),
      })
      .optional(),
    /** Distribution measurements chosen in Especificaciones (walls with furniture, depths, heights, island). */
    dist: z
      .object({
        walls: z.array(z.enum(['A', 'B', 'C', 'D'])).min(1).max(4).optional(),
        baseD: z.number().min(30).max(80).optional(),
        baseH: z.number().min(50).max(100).optional(),
        upperD: z.number().min(20).max(50).optional(),
        aisle: z.number().min(60).max(200).optional(),
        island: z.object({ on: z.boolean().optional(), w: z.number().min(60).max(400).optional(), d: z.number().min(40).max(150).optional() }).optional(),
      })
      .optional(),
    mods: z.array(moduleSchema).max(400),
    mats: z.object({
      cuerpo: z.string().min(1),
      frentes: z.string().min(1),
      encimera: z.string().min(1),
      jaladeras: z.string().min(1),
    }),
    client: z.object({ nombre: z.string().optional(), tel: z.string().optional(), dir: z.string().optional() }).loose().optional(),
    priceAdj: priceAdjSchema.default({ inst: 8, desc: 0, final: null, counter: null, taxRate: null }),
    /** Hardware labels per module id (display only). */
    herr: z.record(z.string(), z.string()).optional(),
    pdfOpts: z.record(z.string(), z.boolean()).optional(),
  })
  .loose();

/** Recipe stored in module_definitions.recipe: fronts layout + feature flags consumed by parts(). */
export const recipeSchema = z.object({
  fr: z.array(frontSegmentSchema).max(12),
  sink: count,
  cook: count,
  appl: flag,
  oven: flag,
  ...applianceFields,
  ...panelsFields,
  /** Set once the stored model was read for boards (models uploaded before boards were saved). */
  pscan: flag,
  draw: drawField,
  ...buildFields,
}).refine((r) => r.fr.length === 0 || Math.abs(r.fr.reduce((a, s) => a + s.f, 0) - 1) < 0.02, {
  message: 'Las fracciones de los frentes (f) deben sumar 1.',
  path: ['fr'],
});

export type ProjectData = z.infer<typeof projectDataSchema>;
export type ModuleInstance = z.infer<typeof moduleSchema>;
export type ModelPanel = z.infer<typeof panelSchema>;
export type Opening = z.infer<typeof openingSchema>;
export type InstallPoint = z.infer<typeof pointSchema>;
export type Recipe = z.infer<typeof recipeSchema>;
